import type { Model } from "@earendil-works/pi-ai";

/**
 * Browser-side handle to the Spark's Qwen3.6 endpoint.
 * baseUrl points at the backend's /api/llm proxy so we don't expose the
 * Spark's address or shared key to the browser.
 *
 * `id` matches the alias vLLM serves (`qwen3.6`), not the underlying repo path.
 */
export const SPARK_PROVIDER = "spark-local";
export const SPARK_MODEL_ID = "qwen3.6";
export const SPARK_CONTEXT_WINDOW = 131_072;

/**
 * The id/name/window/image defaults below only cover the moment before
 * /api/models answers — the server's values (from its env) override them
 * via `overrides`; see llm/models.ts.
 */
export function sparkModel(
	overrides: { id?: string; name?: string; contextWindow?: number; images?: boolean } = {},
): Model<"openai-completions"> {
	return {
		id: overrides.id ?? SPARK_MODEL_ID,
		name: overrides.name ?? "Qwen3.6 35B A3B (FP8)",
		api: "openai-completions",
		provider: SPARK_PROVIDER,
		baseUrl: `${window.location.origin}/api/llm`,
		reasoning: true,
		thinkingLevelMap: {
			off: null,
			minimal: "minimal",
			low: "low",
			medium: "medium",
			high: "high",
		},
		input: overrides.images === false ? ["text"] : ["text", "image"],
		cost: { input: 0, output: 0, cacheRead: 0, cacheWrite: 0 },
		contextWindow: overrides.contextWindow ?? SPARK_CONTEXT_WINDOW,
		maxTokens: 8192,
		// vLLM serves OpenAI-compat but with Qwen3 quirks:
		//  - the chat template only knows `system`/`user`/`assistant`/`tool`
		//    roles, not OpenAI's `developer`
		//  - `<think>` is gated by `chat_template_kwargs.enable_thinking`,
		//    not by OpenAI's `reasoning_effort`
		// Pi-ai's "qwen-chat-template" thinking format handles both for us
		// based on the agent's thinkingLevel.
		compat: {
			supportsDeveloperRole: false,
			supportsReasoningEffort: false,
			thinkingFormat: "qwen-chat-template",
		},
	};
}
